import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ensureWebPushSubscription,
  isWebPushConfigured,
  registerServiceWorker,
} from '../lib/webPush';
import { requestNotificationPermission } from '../lib/browserNotifications';
import {
  dismissNotificationPrompt,
  getNotificationSetupState,
  isNotificationPromptDismissed,
  type NotificationSetupState,
} from '../lib/notificationSetup';
import { isSoftPortalSwitchInGrace } from '../lib/portal';

/** Enable / maintain cross-portal Web Push without CRM-specific polling. */
export function usePortalWebPush() {
  const { profile } = useAuth();
  const [setupState, setSetupState] = useState<NotificationSetupState>(getNotificationSetupState);
  const [promptDismissed, setPromptDismissed] = useState(isNotificationPromptDismissed);

  const dismissPermissionPrompt = useCallback(() => {
    dismissNotificationPrompt();
    setPromptDismissed(true);
  }, []);

  const enableNotifications = useCallback(async () => {
    const next = await requestNotificationPermission();
    const state = getNotificationSetupState();
    setSetupState(state);

    if (next === 'granted' && profile?.id) {
      setPromptDismissed(true);
      await registerServiceWorker();
      await ensureWebPushSubscription(profile.id);
    } else if (state === 'ios-browser') {
      setPromptDismissed(false);
    }
  }, [profile?.id]);

  const showPermissionPrompt = !promptDismissed && setupState !== 'granted';

  useEffect(() => {
    const sync = () => setSetupState(getNotificationSetupState());
    sync();
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  useEffect(() => {
    if (!profile?.id || setupState !== 'granted') return;
    if (isSoftPortalSwitchInGrace()) {
      const timer = window.setInterval(() => {
        if (isSoftPortalSwitchInGrace()) return;
        window.clearInterval(timer);
        void ensureWebPushSubscription(profile.id);
      }, 400);
      return () => window.clearInterval(timer);
    }
    void ensureWebPushSubscription(profile.id);
  }, [profile?.id, setupState]);

  return {
    setupState,
    showPermissionPrompt,
    enableNotifications,
    dismissPermissionPrompt,
    webPushConfigured: isWebPushConfigured(),
  };
}
